import {
  componentOptions,
  isValidComponentType,
  type ComponentName,
  type ComponentProps,
  type UIProps,
} from "app/componentOptions";
import type { RuntimeEnvironment } from "app/Firebase";
import { actions as actionRegistry } from "domain/registry";
import { getGraphQLRecordType, getRecordByTypeAndId } from "lib/api";
import { resolve } from "src/gqty";
import { recordTypeSchema } from "domain/schema/components/recordTypeSchema";
import type { Action } from "lib/action";
import { legacyToBlueprintIcon } from "app/legacyToBlueprintIcon";
import { queryClient } from "app/queryClient";
import { resolvedScreenQueryKey } from "lib/components/ScreenLayout/resolvedScreenQuery";
import type { ResolvedScreenCfg } from "lib/components/ScreenLayout/types";

const routeParamKeys = ["persist", "tenant"] as const;

type ComponentRouteParams = URLSearchParams;

export type ComponentRouteParseResult =
  | {
      type: "none";
    }
  | {
      type: "component";
      componentName: string;
      pathParts: string[];
      params: ComponentRouteParams;
      persist: boolean | null;
      tenantId: string | null;
    }
  | {
      type: "error";
      reason: "empty-component-route" | "malformed-uri";
      tenantId: string | null;
    };

export type ComponentRouteResolutionResult =
  | {
      type: "none";
    }
  | {
      type: "component";
      componentState: {
        componentType: ComponentName;
        componentProps?: ComponentProps<ComponentName>;
        uiProps?: UIProps;
      };
      persist: boolean | null;
      tenantId: string | null;
    }
  | {
      type: "error";
      reason:
        | "unknown-component"
        | "empty-component-route"
        | "malformed-uri"
        | "invalid-route-props"
        | "empty-action"
        | "unknown-action"
        | "action-not-url-enabled"
        | "record-not-found"
        | "screen-not-found"
        | "screen-config-invalid";
      details?: unknown;
    }
  | {
      type: "action";
      actionSlug: string;
      actionId: string | null;
      action: Action<any> | null;
      recordType: string;
      recordId: string | null;
      persist: boolean | null;
      tenantId: string | null;
    };

export type ComponentRouteLocation = {
  pathname: string;
  search: string;
};

type ComponentRouteInput = ComponentRouteLocation & {
  environment: RuntimeEnvironment;
};

type AutoScreenRouteRecord = {
  name: unknown;
  icon?: unknown;
  resolvedScreenCfg?: unknown;
};

type RoutePreparationDependencies = {
  getRecordScreenRecord: (
    recordType: string,
    recordId: string,
  ) => Promise<unknown>;
  getAutoScreenRecord: (
    screenId: string,
  ) => Promise<AutoScreenRouteRecord | null | undefined>;
};

const defaultRoutePreparationDependencies: RoutePreparationDependencies = {
  getRecordScreenRecord: async (recordType, recordId) => {
    return resolve(
      ({ query }) => {
        const record = getRecordByTypeAndId(query, recordType, recordId) as
          | { id?: string }
          | undefined;
        // GQty needs a scalar selection and returns an object when the callback returns undefined.
        return record?.id ?? null;
      },
      { cachePolicy: "no-cache" },
    );
  },
  getAutoScreenRecord: (screenId) =>
    resolve(
      ({ query }) => {
        const screen = query.screen({ id: screenId });

        if (!screen) {
          return null;
        }

        return {
          name: screen.name,
          icon: screen.icon,
          resolvedScreenCfg: screen.resolvedScreenCfg(),
        };
      },
      { cachePolicy: "no-cache" },
    ),
};

function normaliseSearch(search: string) {
  return search.startsWith("?") ? search.slice(1) : search;
}

function trimSlashes(pathname: string) {
  return pathname.replace(/^\/+|\/+$/g, "");
}

function getFirstPathSegment(pathname: string) {
  return trimSlashes(pathname).split("/")[0] ?? "";
}

function getPathSegments(pathname: string) {
  try {
    return trimSlashes(pathname)
      .split("/")
      .map((segment) => decodeURIComponent(segment));
  } catch {
    return null;
  }
}

function parsePersist(params: URLSearchParams) {
  return params.get("persist") === "false" ? false : null;
}

function getFirstParam(params: URLSearchParams, keys: string[]) {
  for (const key of keys) {
    const value = params.get(key);
    if (value !== null) {
      return value;
    }
  }

  return null;
}

function getRecordTabParam(params: URLSearchParams) {
  return getFirstParam(params, ["tab", "tabs", "initialTab"]);
}

function parseComponentPropValue(value: string) {
  if (value === "true") {
    return true;
  }

  if (value === "false") {
    return false;
  }

  if (value === "null") {
    return null;
  }

  if (
    (value.startsWith("{") && value.endsWith("}")) ||
    (value.startsWith("[") && value.endsWith("]"))
  ) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
}

function assignComponentProp(
  props: Record<string, unknown>,
  key: string,
  value: unknown,
) {
  const existing = props[key];

  if (Array.isArray(existing)) {
    existing.push(value);
    return;
  }

  if (existing !== undefined) {
    props[key] = [existing, value];
    return;
  }

  props[key] = value;
}

function resolveCanonicalComponentName(componentName: string) {
  if (isValidComponentType(componentName)) {
    return componentName;
  }

  const normalisedComponentName = componentName.toLowerCase();
  return (Object.keys(componentOptions) as ComponentName[]).find(
    (candidate) => candidate.toLowerCase() === normalisedComponentName,
  );
}

function normaliseRecordScreenRouteParams(params: URLSearchParams) {
  const normalisedParams = new URLSearchParams(params);
  const initialTab = getRecordTabParam(normalisedParams);

  normalisedParams.delete("tab");
  normalisedParams.delete("tabs");
  normalisedParams.delete("action");

  if (initialTab !== null && !normalisedParams.has("initialTab")) {
    normalisedParams.set("initialTab", initialTab);
  }

  if (!normalisedParams.has("context")) {
    normalisedParams.set("context", "window");
  }

  const recordType = normalisedParams.get("recordType");
  const resolvedRecordType = recordType
    ? resolveRecordTypeSlug(recordType)
    : null;

  if (resolvedRecordType) {
    normalisedParams.set("recordType", resolvedRecordType);
  }

  return normalisedParams;
}

function getActionModuleName(actionId: string) {
  const [, moduleName] = actionId.split(".");
  return moduleName ?? "";
}

export function getActionUrlSlug(recordType: string, actionId: string) {
  const moduleName = getActionModuleName(actionId);
  const normalizedRecordType = recordType.toLowerCase();
  const normalizedModuleName = moduleName.toLowerCase();
  const recordTypeIndex = normalizedModuleName.indexOf(normalizedRecordType);

  if (recordTypeIndex === -1) {
    return moduleName;
  }

  return (
    moduleName.slice(0, recordTypeIndex) +
    moduleName.slice(recordTypeIndex + recordType.length)
  );
}

function normaliseComponentRouteParams(
  componentName: ComponentName,
  params: URLSearchParams,
) {
  if (componentName === "RecordScreen") {
    return normaliseRecordScreenRouteParams(params);
  }

  return params;
}

function resolveComponentProps<T extends ComponentName>(
  componentName: T,
  params: URLSearchParams,
) {
  const normalisedParams = normaliseComponentRouteParams(componentName, params);
  const props: Record<string, unknown> = {
    ...(componentOptions[componentName].defaultComponentProps ?? {}),
  };

  normalisedParams.forEach((value, key) => {
    if ((routeParamKeys as readonly string[]).includes(key)) {
      return;
    }

    assignComponentProp(props, key, parseComponentPropValue(value));
  });

  return props as ComponentProps<T>;
}

function isActionRouteComponentName(componentName: string) {
  return componentName.toLowerCase() === "action";
}

function resolveActionRoute(
  parsed: Extract<ComponentRouteParseResult, { type: "component" }>,
): ComponentRouteResolutionResult {
  if (parsed.params.has("action")) {
    return {
      type: "error",
      reason: "invalid-route-props",
      details: {
        componentName: parsed.componentName,
        pathParts: parsed.pathParts,
      },
    };
  }

  const [recordTypeSlug, recordIdOrActionSlug, actionSlug] = parsed.pathParts;
  const isRecordScopedRoute = actionSlug !== undefined;
  const resolvedActionSlug = isRecordScopedRoute
    ? actionSlug
    : recordIdOrActionSlug;

  if (!resolvedActionSlug || resolvedActionSlug.trim() === "") {
    return {
      type: "error",
      reason: "empty-action",
    };
  }

  const recordType =
    typeof recordTypeSlug === "string"
      ? resolveRecordTypeSlug(recordTypeSlug)
      : null;

  if (
    !recordType ||
    parsed.pathParts.length > 3 ||
    (isRecordScopedRoute && !recordIdOrActionSlug)
  ) {
    return {
      type: "error",
      reason: "invalid-route-props",
      details: {
        componentName: parsed.componentName,
        pathParts: parsed.pathParts,
      },
    };
  }

  const routeRecordId = isRecordScopedRoute
    ? (recordIdOrActionSlug ?? null)
    : null;

  return {
    type: "action",
    actionSlug: resolvedActionSlug,
    actionId: null,
    action: null,
    recordType,
    recordId: routeRecordId,
    persist: parsed.persist,
    tenantId: parsed.tenantId,
  };
}

function getRouteUiProps(
  componentName: ComponentName,
  props: Record<string, unknown>,
): UIProps | undefined {
  const configuredLabel =
    componentOptions[componentName].defaultUiOptions.label;

  if (configuredLabel) {
    return undefined;
  }

  const label = props.label ?? props.initialName;

  if (typeof label === "string" && label.trim() !== "") {
    return { label };
  }

  // An explicit label prevents data-backed screens from supplying their loaded name.
  return undefined;
}

export function getDevelopmentTenantId(tenantParam: string | null) {
  if (!tenantParam) {
    return null;
  }

  return tenantParam.split("/")[0] || null;
}

function parseDevelopmentTenantRoute(
  search: string,
): ComponentRouteParseResult {
  const searchParams = new URLSearchParams(normaliseSearch(search));
  const tenantParam = searchParams.get("tenant");
  const tenantId = getDevelopmentTenantId(tenantParam);

  if (!tenantParam || !tenantParam.includes("/")) {
    return {
      type: "none",
    };
  }

  return {
    type: "error",
    reason: "malformed-uri",
    tenantId,
  };
}

function parsePathRoute({
  pathname,
  search,
  tenantId = null,
  routeParamKeysToRemove = [],
}: ComponentRouteLocation & {
  tenantId?: string | null;
  routeParamKeysToRemove?: string[];
}): ComponentRouteParseResult {
  const componentName = getFirstPathSegment(pathname);
  const pathSegments = getPathSegments(pathname);

  if (pathSegments === null) {
    return {
      type: "error",
      reason: "malformed-uri",
      tenantId,
    };
  }

  const [, ...pathParts] = pathSegments;

  if (!componentName) {
    return {
      type: "none",
    };
  }

  const params = new URLSearchParams(normaliseSearch(search));

  routeParamKeysToRemove.forEach((key) => {
    params.delete(key);
  });

  return {
    type: "component",
    componentName,
    pathParts,
    params,
    persist: parsePersist(params),
    tenantId,
  };
}

export function parseComponentRoute({
  environment,
  pathname,
  search,
}: ComponentRouteInput): ComponentRouteParseResult {
  if (environment === "DEVELOPMENT") {
    const tenantId = getDevelopmentTenantId(
      new URLSearchParams(normaliseSearch(search)).get("tenant"),
    );

    if (getFirstPathSegment(pathname)) {
      return parsePathRoute({
        pathname,
        search,
        tenantId,
        routeParamKeysToRemove: ["tenant"],
      });
    }

    return parseDevelopmentTenantRoute(search);
  }

  return parsePathRoute({ pathname, search });
}

export function resolveRecordTypeSlug(recordTypeSlug: string) {
  const normalizedSlug = recordTypeSlug.toLowerCase();

  const maybeRecordType = recordTypeSchema.options.find(
    (recordType) => recordType.toLowerCase() === normalizedSlug,
  );

  if (!maybeRecordType) {
    return null;
  }

  try {
    return getGraphQLRecordType(maybeRecordType).name;
  } catch {
    return null;
  }
}

export function resolveComponentRoute(
  input: ComponentRouteInput,
): ComponentRouteResolutionResult {
  const parsed = parseComponentRoute(input);

  if (parsed.type === "none") {
    return parsed;
  }

  if (parsed.type === "error") {
    return {
      type: "error",
      reason: parsed.reason,
    };
  }

  if (isActionRouteComponentName(parsed.componentName)) {
    return resolveActionRoute(parsed);
  }

  if (parsed.params.has("action")) {
    return {
      type: "error",
      reason: "invalid-route-props",
      details: {
        componentName: parsed.componentName,
      },
    };
  }

  const componentName = resolveCanonicalComponentName(parsed.componentName);

  if (!componentName) {
    return {
      type: "error",
      reason: "unknown-component",
      details: {
        componentName: parsed.componentName,
      },
    };
  }

  const routeResolvedState = componentOptions[componentName].routing?.resolve?.(
    {
      pathParts: parsed.pathParts,
      searchParams: parsed.params,
    },
  );
  const componentProps =
    routeResolvedState?.componentProps ??
    resolveComponentProps(componentName, parsed.params);
  const uiProps =
    routeResolvedState?.uiProps ??
    getRouteUiProps(componentName, componentProps);
  const routeComponentType = routeResolvedState?.componentType ?? componentName;

  return {
    type: "component",
    componentState: {
      componentType: routeComponentType,
      componentProps,
      uiProps,
    },
    persist: parsed.persist,
    tenantId: parsed.tenantId,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function prepareRecordScreenRoute(
  resolution: Extract<ComponentRouteResolutionResult, { type: "component" }>,
  dependencies: RoutePreparationDependencies,
): Promise<ComponentRouteResolutionResult> {
  const props = resolution.componentState.componentProps;

  if (!isRecord(props)) {
    return {
      type: "error",
      reason: "invalid-route-props",
      details: {
        componentType: resolution.componentState.componentType,
        props,
      },
    };
  }

  const routeProps: Record<string, unknown> = props;
  const recordTypeResult = recordTypeSchema.safeParse(routeProps.recordType);
  const recordId = routeProps.recordId;

  if (!recordTypeResult.success || typeof recordId !== "string") {
    return {
      type: "error",
      reason: "invalid-route-props",
      details: {
        componentType: resolution.componentState.componentType,
        props: routeProps,
      },
    };
  }

  const record = await dependencies.getRecordScreenRecord(
    recordTypeResult.data,
    recordId,
  );

  if (!record) {
    return {
      type: "error",
      reason: "record-not-found",
      details: {
        recordType: recordTypeResult.data,
        recordId,
      },
    };
  }

  return resolution;
}

async function prepareAutoScreenRoute(
  resolution: Extract<ComponentRouteResolutionResult, { type: "component" }>,
  dependencies: RoutePreparationDependencies,
): Promise<ComponentRouteResolutionResult> {
  const props = resolution.componentState.componentProps;

  if (!isRecord(props)) {
    return resolution;
  }

  const routeProps: Record<string, unknown> = props;

  if (routeProps.initialScreenCfg !== undefined) {
    return isRecord(routeProps.initialScreenCfg)
      ? resolution
      : {
          type: "error",
          reason: "screen-config-invalid",
          details: {
            screenId: routeProps.screenId,
            initialScreenCfg: routeProps.initialScreenCfg,
          },
        };
  }

  if (typeof routeProps.screenId !== "string") {
    return resolution;
  }

  const screenId = routeProps.screenId;
  const screenQueryKey = resolvedScreenQueryKey({ screenId });
  const screen = await dependencies.getAutoScreenRecord(screenId);

  if (!screen) {
    return {
      type: "error",
      reason: "screen-not-found",
      details: {
        screenId,
      },
    };
  }

  if (typeof screen.resolvedScreenCfg !== "string") {
    return {
      type: "error",
      reason: "screen-config-invalid",
      details: {
        screenId,
        screen,
      },
    };
  }

  try {
    const screenCfg = JSON.parse(screen.resolvedScreenCfg) as ResolvedScreenCfg;
    queryClient.setQueryData(screenQueryKey, {
      name: screen.name,
      screenCfg,
    });

    return {
      ...resolution,
      componentState: {
        ...resolution.componentState,
        componentProps: {
          ...props,
        },
        uiProps: resolution.componentState.uiProps ?? {
          label: String(screen.name),
          icon: legacyToBlueprintIcon(
            typeof screen.icon === "string" ? screen.icon : "blank",
          ),
        },
      },
    };
  } catch {
    return {
      type: "error",
      reason: "screen-config-invalid",
      details: {
        screenId,
        screen,
      },
    };
  }
}

async function prepareActionRoute(
  resolution: Extract<ComponentRouteResolutionResult, { type: "action" }>,
): Promise<ComponentRouteResolutionResult> {
  const actionEntries = Object.entries(
    await actionRegistry.getByRecordTypeAsync(resolution.recordType),
  );
  const routeActionSlug = resolution.actionSlug.toLowerCase();
  const actionEntry = actionEntries.find(([actionId]) =>
    [
      actionId,
      getActionModuleName(actionId),
      getActionUrlSlug(resolution.recordType, actionId),
    ]
      .map((slug) => slug.toLowerCase())
      .includes(routeActionSlug),
  );

  if (!actionEntry) {
    return {
      type: "error",
      reason: "unknown-action",
      details: {
        recordType: resolution.recordType,
        actionSlug: resolution.actionSlug,
      },
    };
  }

  const [actionId, action] = actionEntry;

  if (!action.canInvokeViaUrl) {
    return {
      type: "error",
      reason: "action-not-url-enabled",
      details: {
        recordType: resolution.recordType,
        actionSlug: resolution.actionSlug,
        actionId,
      },
    };
  }

  if (action.scope === "global" && resolution.recordId !== null) {
    return {
      type: "error",
      reason: "invalid-route-props",
      details: {
        recordType: resolution.recordType,
        actionSlug: resolution.actionSlug,
        actionId,
        scope: action.scope,
        recordId: resolution.recordId,
      },
    };
  }

  if (action.scope !== "global" && resolution.recordId === null) {
    return {
      type: "error",
      reason: "invalid-route-props",
      details: {
        recordType: resolution.recordType,
        actionSlug: resolution.actionSlug,
        actionId,
        scope: action.scope ?? "record",
      },
    };
  }

  return {
    ...resolution,
    actionId,
    action,
  };
}

export async function prepareComponentRouteResolution(
  resolution: ComponentRouteResolutionResult,
  dependencies: RoutePreparationDependencies = defaultRoutePreparationDependencies,
): Promise<ComponentRouteResolutionResult> {
  if (resolution.type === "action") {
    return prepareActionRoute(resolution);
  }

  if (resolution.type === "component") {
    switch (resolution.componentState.componentType) {
      case "RecordScreen":
        return prepareRecordScreenRoute(resolution, dependencies);
      case "AutoScreen":
        return prepareAutoScreenRoute(resolution, dependencies);
      default:
        return resolution;
    }
  }

  return resolution;
}

export function getComponentRouteSignature(input: ComponentRouteInput) {
  const parsed = parseComponentRoute(input);

  switch (parsed.type) {
    case "none":
      return null;
    case "error":
      return `${input.environment}:error:${parsed.reason}`;
    case "component":
      return `${input.environment}:component:${parsed.componentName}:${parsed.pathParts.join("/")}:${parsed.params.toString()}`;
  }
}

export function getPersistFalseComponentHref(
  href: string,
  environment: RuntimeEnvironment,
  tenantId: string | null,
) {
  const url = new URL(href);
  const hash = url.hash || (href.endsWith("#") ? "#" : "");

  if (environment === "DEVELOPMENT" && tenantId) {
    const params = new URLSearchParams();
    params.set("tenant", tenantId);
    return `/?${params.toString()}${hash}`;
  }

  return `/${hash}`;
}

export function getComponentRouteErrorMessage(
  reason: Extract<ComponentRouteResolutionResult, { type: "error" }>["reason"],
) {
  switch (reason) {
    case "record-not-found":
      return "Record not found. The link may be broken.";
    case "empty-action":
      return "Invalid link: empty action.";
    case "unknown-action":
      return "Unknown action in link.";
    case "action-not-url-enabled":
      return "This action cannot be opened from a URL.";
    case "unknown-component":
    case "empty-component-route":
    case "malformed-uri":
    case "invalid-route-props":
    case "screen-not-found":
    case "screen-config-invalid":
      return "Invalid link. This link may be broken.";
  }
}

export function getComponentRouteErrorLogMessage(
  reason: Extract<ComponentRouteResolutionResult, { type: "error" }>["reason"],
) {
  switch (reason) {
    case "unknown-component":
      return "Component link failed: unknown component.";
    case "empty-component-route":
      return "Component link failed: empty component route.";
    case "malformed-uri":
      return "Component link failed: malformed URI.";
    case "invalid-route-props":
      return "Component link failed: invalid route props.";
    case "empty-action":
      return "Component link failed: empty action.";
    case "unknown-action":
      return "Component link failed: unknown action.";
    case "action-not-url-enabled":
      return "Component link failed: action is not URL enabled.";
    case "record-not-found":
      return "Component link failed: record not found.";
    case "screen-not-found":
      return "Component link failed: screen not found.";
    case "screen-config-invalid":
      return "Component link failed: invalid screen config.";
  }
}
